/**
 * Fractional indexing — generates string keys that can be subdivided
 * indefinitely. Used by the kanban boards (tasks and projects) to slot a
 * dragged card between two others without renumbering the column. Ported
 * from the public-domain `fractional-indexing` package
 * (https://github.com/rocicorp/fractional-indexing, CC0), which in turn
 * implements David Greenspan's algorithm at
 * https://observablehq.com/@dgreensp/implementing-fractional-indexing.
 *
 * Keys are base-62 strings with a 1-char "integer head" prefix:
 * - `A`..`Z` denote a fractional part of length 2..27 (decreasing).
 * - `a`..`z` denote a fractional part of length 2..27 (increasing).
 * The very first key from `keyBetween(null, null)` is `'a0'`. Two adjacent
 * keys can always have a new key inserted between them by appending more
 * fractional digits, so the scheme has unbounded headroom.
 *
 * The kanban views import `keyBetween` from here directly, so the browser
 * can compute a key for an optimistic drag without round-tripping to the
 * backend. The module is intentionally dependency-free — nothing here
 * touches the Convex runtime — which is what makes it safe to pull into
 * the client bundle.
 */

const BASE_62_DIGITS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
const ZERO = '0'
const MAX_DIGIT = 'z'

const getIntegerLength = (head: string): number => {
  if (head >= 'a' && head <= 'z') {
    return head.charCodeAt(0) - 'a'.charCodeAt(0) + 2
  }
  if (head >= 'A' && head <= 'Z') {
    return 'Z'.charCodeAt(0) - head.charCodeAt(0) + 2
  }
  throw new Error(`invalid order key head: ${head}`)
}

const getIntegerPart = (key: string): string => {
  const length = getIntegerLength(key[0])
  if (length > key.length) {
    throw new Error(`invalid order key: ${key}`)
  }
  return key.slice(0, length)
}

const validateInteger = (int: string): void => {
  if (int.length !== getIntegerLength(int[0])) {
    throw new Error(`invalid integer part of order key: ${int}`)
  }
}

const validateOrderKey = (key: string): void => {
  // Every character must be a base-62 digit. The upstream algorithm never
  // checks this because it only ever sees keys it generated; we take keys
  // from clients, and one carrying an out-of-alphabet character passes every
  // other rule while breaking ordering — `incrementInteger` reads its digit
  // index as -1, so `keyBetween('a~', null)` returns `a0`, which sorts BEFORE
  // the key it was supposed to follow.
  if (![...key].every((character) => BASE_62_DIGITS.includes(character))) {
    throw new Error(`invalid order key: ${key}`)
  }
  if (key === `A${ZERO.repeat(26)}`) {
    throw new Error(`invalid order key: ${key}`)
  }
  const int = getIntegerPart(key)
  const frac = key.slice(int.length)
  if (frac.slice(-1) === ZERO) {
    throw new Error(`invalid order key: ${key}`)
  }
}

/** Lexicographic midpoint of fractional parts `a` and `b`, with `a < b`. */
const midpoint = (a: string, b: string | null): string => {
  if (b !== null && a >= b) {
    throw new Error(`${a} >= ${b}`)
  }
  if (a.slice(-1) === ZERO || (b && b.slice(-1) === ZERO)) {
    throw new Error('trailing zero')
  }
  if (b) {
    // Strip the longest shared prefix and recurse on the divergent tails.
    let n = 0
    while ((a[n] || ZERO) === b[n]) {
      n++
    }
    if (n > 0) {
      return b.slice(0, n) + midpoint(a.slice(n), b.slice(n))
    }
  }
  const aDigit = a ? BASE_62_DIGITS.indexOf(a[0]) : 0
  const bDigit = b === null ? BASE_62_DIGITS.length : BASE_62_DIGITS.indexOf(b[0])
  if (bDigit - aDigit > 1) {
    return BASE_62_DIGITS[Math.round(0.5 * (aDigit + bDigit))]
  }
  if (b && b.length > 1) {
    return b.slice(0, 1)
  }
  // `b` is null or a single digit. Extend `a` further and recurse.
  return BASE_62_DIGITS[aDigit] + midpoint(a.slice(1), null)
}

const incrementInteger = (x: string): string | null => {
  validateInteger(x)
  const [head] = x
  const digs = x.slice(1).split('')
  let carry = true
  for (let i = digs.length - 1; carry && i >= 0; i--) {
    const d = BASE_62_DIGITS.indexOf(digs[i]) + 1
    if (d === BASE_62_DIGITS.length) {
      digs[i] = ZERO
    } else {
      digs[i] = BASE_62_DIGITS[d]
      carry = false
    }
  }
  if (!carry) {
    return head + digs.join('')
  }
  if (head === 'Z') {
    return `a${ZERO}`
  }
  if (head === 'z') {
    return null
  }
  const nextHead = String.fromCharCode(head.charCodeAt(0) + 1)
  if (nextHead > 'a') {
    digs.push(ZERO)
  } else {
    digs.pop()
  }
  return nextHead + digs.join('')
}

const decrementInteger = (x: string): string | null => {
  validateInteger(x)
  const [head] = x
  const digs = x.slice(1).split('')
  let borrow = true
  for (let i = digs.length - 1; borrow && i >= 0; i--) {
    const d = BASE_62_DIGITS.indexOf(digs[i]) - 1
    if (d === -1) {
      digs[i] = MAX_DIGIT
    } else {
      digs[i] = BASE_62_DIGITS[d]
      borrow = false
    }
  }
  if (!borrow) {
    return head + digs.join('')
  }
  if (head === 'a') {
    return `Z${MAX_DIGIT}`
  }
  if (head === 'A') {
    return null
  }
  const prevHead = String.fromCharCode(head.charCodeAt(0) - 1)
  if (prevHead < 'Z') {
    digs.push(MAX_DIGIT)
  } else {
    digs.pop()
  }
  return prevHead + digs.join('')
}

/**
 * Returns a key strictly between `a` and `b`. Either side may be `null` to
 * mean "no bound" — pass both `null` to seed a fresh column. The result is
 * compatible with keys produced by the `fractional-indexing` package the
 * codebase used previously: the algorithm is identical, so existing keys in
 * the database don't need to be regenerated.
 */
export const keyBetween = (a: string | null, b: string | null): string => {
  if (a !== null) {
    validateOrderKey(a)
  }
  if (b !== null) {
    validateOrderKey(b)
  }
  if (a !== null && b !== null && a >= b) {
    throw new Error(`${a} >= ${b}`)
  }
  if (a === null) {
    if (b === null) {
      return `a${ZERO}`
    }
    const ib = getIntegerPart(b)
    const fb = b.slice(ib.length)
    if (ib === `A${ZERO.repeat(26)}`) {
      return ib + midpoint('', fb)
    }
    if (ib < b) {
      return ib
    }
    const res = decrementInteger(ib)
    if (res === null) {
      throw new Error('cannot decrement any more')
    }
    return res
  }
  if (b === null) {
    const ia = getIntegerPart(a)
    const fa = a.slice(ia.length)
    const i = incrementInteger(ia)
    return i === null ? ia + midpoint(fa, null) : i
  }
  const ia = getIntegerPart(a)
  const fa = a.slice(ia.length)
  const ib = getIntegerPart(b)
  const fb = b.slice(ib.length)
  if (ia === ib) {
    return ia + midpoint(fa, fb)
  }
  const i = incrementInteger(ia)
  if (i === null) {
    throw new Error('cannot increment any more')
  }
  if (i < b) {
    return i
  }
  return ia + midpoint(fa, null)
}

/** N keys in ascending order, strictly between `a` and `b`. */
export const keysBetween = (a: string | null, b: string | null, n: number): string[] => {
  if (n === 0) {
    return []
  }
  if (n === 1) {
    return [keyBetween(a, b)]
  }
  if (b === null) {
    let c = keyBetween(a, b)
    const result = [c]
    for (let i = 0; i < n - 1; i++) {
      c = keyBetween(c, b)
      result.push(c)
    }
    return result
  }
  if (a === null) {
    let c = keyBetween(a, b)
    const result = [c]
    for (let i = 0; i < n - 1; i++) {
      c = keyBetween(a, c)
      result.push(c)
    }
    result.reverse()
    return result
  }
  const mid = Math.floor(n / 2)
  const c = keyBetween(a, b)
  return [...keysBetween(a, c, mid), c, ...keysBetween(c, b, n - mid - 1)]
}

/**
 * Whether `key` is a well-formed order key — precisely the strings
 * `keyBetween` accepts as a bound, checked by running that same validation.
 * Keeping the two in lockstep is the point: anything this returns `true` for
 * can be passed to `keyBetween` without it throwing on the bound itself.
 */
export const isValidOrderKey = (key: string): boolean => {
  try {
    validateOrderKey(key)
    return true
  } catch {
    return false
  }
}

/**
 * Throws unless `key` is well-formed. Every mutation that accepts an `order`
 * key from a client calls this: the keys are computed browser-side and
 * arrive as a bare `v.string()`, so without it any string at all lands in
 * the column — and a single malformed key then breaks every later
 * `keyBetween` that reads it as a bound, which is how creating a task in a
 * column stops working entirely (PLANNO-API-1P).
 */
export const assertOrderKey = (key: string): void => {
  if (!isValidOrderKey(key)) {
    throw new Error(`invalid order key: ${key}`)
  }
}

/**
 * The greatest well-formed key in `keys`, or `null` when there is none —
 * the "place me after everything" bound for `keyBetween`.
 *
 * Malformed and absent keys are SKIPPED rather than thrown on. A row whose
 * key predates this scheme, or was written before `assertOrderKey` guarded
 * the mutations, must not be able to stop new rows from being created: the
 * worst it can do is sort oddly until `migrations:runRepairInvalidOrderKeys`
 * clears it.
 */
export const highestOrderKey = (keys: Iterable<string | undefined>): string | null => {
  let highest: string | null = null
  for (const key of keys) {
    if (key === undefined || !isValidOrderKey(key)) {
      continue
    }
    if (highest === null || key > highest) {
      highest = key
    }
  }
  return highest
}

/** The lowest well-formed key in `keys` — `highestOrderKey`'s mirror, for "place me first". */
export const lowestOrderKey = (keys: Iterable<string | undefined>): string | null => {
  let lowest: string | null = null
  for (const key of keys) {
    if (key === undefined || !isValidOrderKey(key)) {
      continue
    }
    if (lowest === null || key < lowest) {
      lowest = key
    }
  }
  return lowest
}

/** `key` when it is well-formed, `null` otherwise — a bound `keyBetween` can take. */
export const orderKeyBound = (key: string | undefined): string | null =>
  key !== undefined && isValidOrderKey(key) ? key : null
